import { ActivityIndicator } from 'components/BlockingLoader/ActivityIndicator';
import { Icon, ICONS } from 'components/Icon';
import { Pressable } from 'components/Pressable';
import { Image, ImageContentFit, useImage } from 'expo-image';
import * as React from 'react';
import {
  StyleSheet,
  StyleProp,
  View,
  ViewStyle,
  DimensionValue,
} from 'react-native';

type Props = {
  imageUrl: string;
  width: DimensionValue;
  height: DimensionValue;
  retryOnError?: boolean;
  icon?: ICONS;
  iconColor?: string;
  style?: StyleProp<ViewStyle>;
  contentFit?: ImageContentFit;
  iconSize?: number;
  onPress?: () => void;
} & (
  | {
      rounded?: boolean;
      borderRadius?: undefined;
    }
  | {
      rounded?: undefined;
      borderRadius: number;
    }
);

const RETRY_DELAY_MS = 2_000;
const MAX_RETRY_ATTEMPTS = 30;

export const CloudThumbnail: React.FC<Props> = React.memo(
  ({
    icon = ICONS.ImageThin,
    iconColor,
    imageUrl,
    rounded = false,
    borderRadius,
    contentFit = 'contain',
    width,
    height,
    retryOnError = false,
    iconSize,
    style,
    onPress,
  }) => {
    const [imageUrlState, setImageUrlState] = React.useState<string>(imageUrl);
    const [state, setState] = React.useState<'loading' | 'errored'>('loading');
    const retryAttemptsRef = React.useRef(0);
    const retryTimerRef =
      React.useRef<ReturnType<typeof setTimeout>>(undefined);

    const clearRetryTimer = React.useCallback(() => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = undefined;
      }
    }, []);

    const image = useImage(
      imageUrlState,
      {
        onError(_error, retry) {
          setState('errored');
          if (retryOnError && retryAttemptsRef.current < MAX_RETRY_ATTEMPTS) {
            retryAttemptsRef.current += 1;
            retryTimerRef.current = setTimeout(() => {
              retryTimerRef.current = undefined;
              retry();
            }, RETRY_DELAY_MS);
          }
        },
      },
      [imageUrlState],
    );

    React.useEffect(() => {
      if (imageUrlState !== imageUrl) {
        clearRetryTimer();
        retryAttemptsRef.current = 0;
        setImageUrlState(imageUrl);
        setState('loading');
      }
    }, [clearRetryTimer, imageUrl, imageUrlState]);

    React.useEffect(() => {
      if (!retryOnError) {
        clearRetryTimer();
      }
    }, [clearRetryTimer, retryOnError]);

    React.useEffect(() => clearRetryTimer, [clearRetryTimer]);

    const widthInt: number = Number.isInteger(width) ? (width as number) : 0;
    const heightInt: number = Number.isInteger(height) ? (height as number) : 0;
    const getIconSize = React.useCallback(
      (dimension: number) => Math.round(dimension * 0.6),
      [],
    );

    return (
      <Pressable
        style={[
          styles.container,
          {
            borderRadius: rounded ? 9999 : borderRadius,
            width: width || '100%',
            height: width || '100%',
            overflow: 'hidden',
          },
          style,
        ]}
        onPress={onPress && imageUrl ? onPress : undefined}
      >
        {image ? (
          <Image
            style={{
              width: '100%',
              height: '100%',
            }}
            contentFit={contentFit}
            contentPosition="center"
            source={image}
          />
        ) : state === 'loading' ? (
          <View
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              right: 0,
              bottom: 0,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ActivityIndicator size={30} />
          </View>
        ) : (
          <Icon
            source={icon}
            size={
              iconSize ||
              (widthInt && heightInt
                ? getIconSize(Math.min(widthInt, heightInt))
                : widthInt
                  ? getIconSize(widthInt)
                  : heightInt
                    ? getIconSize(heightInt)
                    : getIconSize(80))
            }
            color={iconColor || '#bbbbbb'}
          />
        )}
      </Pressable>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#ddd',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
